import { ElementType, Element, Grid, Particle } from './types';

export const GRID_WIDTH = 180;
export const GRID_HEIGHT = 120;
export const SIMULATION_INTERVAL = 50; // ms

// Temperature Constants (Celsius)
export const AMBIENT_TEMPERATURE = 20;
export const FIRE_TEMPERATURE = 2000;
export const LAVA_TEMPERATURE = 1800;
export const LAVA_SOLIDIFY_POINT = 1000;
export const ICE_TARGET_TEMPERATURE = -50;
export const SAND_MELT_POINT = 1700;
export const ICE_MELT_POINT = 0;
export const WATER_BOIL_POINT = 100;
export const LIGHTNING_TEMPERATURE = 10000;
export const SNOW_VISUAL_THRESHOLD = -1;
export const HOT_ZONE_THRESHOLD = 500;
export const COLD_ZONE_THRESHOLD = -10;
export const OIL_IGNITE_POINT = 300;
export const GUNPOWDER_IGNITE_POINT = 200;
export const METAL_MELT_POINT = 1500;
// NEW ELEMENT TEMPERATURES
export const WAX_MELT_POINT = 60;
export const COAL_IGNITE_POINT = 400;
export const MAGMA_SOLIDIFY_POINT = 1200;

export const INITIAL_ELEMENTS: Element[] = [
  { id: ElementType.EMPTY, name: 'Eraser', color: '#111827', description: 'Removes elements.', density: 0, thermalConductivity: 1.0 },
  { id: ElementType.WALL, name: 'Wall', color: '#4b5563', description: 'An indestructible barrier.', density: 100, thermalConductivity: 0.01 },
  { id: ElementType.SAND, name: 'Sand', color: '#d4a373', description: 'A simple falling element. Melts into Glass.', density: 1.5, thermalConductivity: 0.2 },
  { id: ElementType.DIRT, name: 'Dirt', color: '#967969', description: 'Dry soil. Supports plant life when wet.', density: 1.4, thermalConductivity: 0.2 },
  { id: ElementType.WATER, name: 'Water', color: '#3b82f6', description: 'Flows and spreads. Freezes into Ice.', density: 1.0, thermalConductivity: 0.7 },
  { id: ElementType.OIL, name: 'Oil', color: '#1e1b4b', description: 'Flammable liquid. Floats on water. Burns intensely.', density: 0.85, thermalConductivity: 0.3, flammable: true },
  { id: ElementType.MUD, name: 'Mud', color: '#5c4033', description: 'Wet soil. Slowly flows and dries into Dirt.', density: 1.3, thermalConductivity: 0.4 },
  { id: ElementType.ICE, name: 'Ice', color: '#a5f3fc', description: 'Solid ice. Melts into Water. Actively cools surroundings.', density: 0.92, thermalConductivity: 0.9 },
  { id: ElementType.GLASS, name: 'Glass', color: '#1f2937', description: 'Transparent block formed from molten sand.', density: 2.4, thermalConductivity: 0.08 },
  { id: ElementType.STONE, name: 'Stone', color: '#6b7280', description: 'A heavy, solid block that falls.', density: 2.5, thermalConductivity: 0.05 },
  { id: ElementType.METAL, name: 'Metal', color: '#94a3b8', description: 'Heavy conductor. Heats and cools rapidly.', density: 7.0, thermalConductivity: 0.95 },
  { id: ElementType.LAVA, name: 'Lava', color: '#f97316', description: 'Molten rock. Burns things and cools into stone.', density: 3.0, thermalConductivity: 0.8 },
  { id: ElementType.WOOD, name: 'Wood', color: '#8c5a3b', description: 'A flammable solid material.', density: 0.9, thermalConductivity: 0.1, flammable: true },
  { id: ElementType.GUNPOWDER, name: 'Gunpowder', color: '#374151', description: 'Explosive! Ignites from heat or fire.', density: 1.1, thermalConductivity: 0.15, flammable: true },
  { id: ElementType.FIRE, name: 'Fire', color: '#ef4444', description: 'Spreads to flammable materials.', density: 0.2, thermalConductivity: 0.5 },
  { id: ElementType.SMOKE, name: 'Smoke', color: '#52525b', description: 'A light gas that rises and diffuses.', density: 0.1, thermalConductivity: 0.8 },
  { id: ElementType.STEAM, name: 'Steam', color: '#a3a3a3', description: 'Hot water vapor that rises.', density: 0.05, thermalConductivity: 0.9 },
  { id: ElementType.PLANT, name: 'Plant', color: '#4ade80', description: 'Grows with water and soil.', density: 0.8, thermalConductivity: 0.5, flammable: true },
  { id: ElementType.ACID, name: 'Acid', color: '#a3e635', description: 'Corrodes most materials on contact.', density: 1.2, thermalConductivity: 0.7 },
  // NEW ELEMENTS
  { id: ElementType.WAX, name: 'Wax', color: '#f5e6d3', description: 'Solid that melts when heated, hardens when cool.', density: 0.9, thermalConductivity: 0.15 },
  { id: ElementType.FUSE, name: 'Fuse', color: '#d4a574', description: 'Slow-burning solid for timing bombs. Burns gradually.', density: 0.8, thermalConductivity: 0.1, flammable: true },
  { id: ElementType.COAL, name: 'Coal', color: '#2d2d2d', description: 'Long-burning fuel. Burns 3x longer than wood.', density: 1.3, thermalConductivity: 0.2, flammable: true },
  { id: ElementType.SALT, name: 'Salt', color: '#ffffff', description: 'Dissolves in water. Heavy crystalline solid.', density: 2.1, thermalConductivity: 0.3 },
  { id: ElementType.SLIME, name: 'Slime', color: '#39ff14', description: 'Bouncy, sticky liquid that sticks to surfaces.', density: 1.1, thermalConductivity: 0.4 },
  { id: ElementType.MAGMA, name: 'Magma', color: '#8b0000', description: 'Heavier lava that cools slower into obsidian.', density: 3.5, thermalConductivity: 0.9 },
  { id: ElementType.ELECTRIC_SPARK, name: 'Electric Spark', color: '#ffff00', description: 'Travels through metal, ignites flammables.', density: 0.05, thermalConductivity: 1.0 },
  // SPELLS
  { id: ElementType.TORNADO, name: 'Tornado', color: '#a7f3d0', description: 'Drag to cast a moving vortex.', density: 0 },
  { id: ElementType.LIGHTNING, name: 'Lightning', color: '#fde047', description: 'Drag to summon a lightning bolt.', density: 0 },
  { id: ElementType.METEOR, name: 'Meteor', color: '#dc2626', description: 'Click to summon a falling fireball.', density: 0 },
  { id: ElementType.BOMB, name: 'Bomb', color: '#f59e0b', description: 'Click to place. Explodes on contact with fire.', density: 0 },
  { id: ElementType.BLACKHOLE, name: 'Black Hole', color: '#3b0764', description: 'Drag to create a consuming void.', density: 0 },
  { id: ElementType.RAINCLOUD, name: 'Rain Cloud', color: '#60a5fa', description: 'Click to summon a drifting rain cloud.', density: 0 },
  // NEW SPELLS
  { id: ElementType.FREEZE_RAY, name: 'Freeze Ray', color: '#00ffff', description: 'Drag to freeze liquids instantly.', density: 0 },
  { id: ElementType.EARTHQUAKE, name: 'Earthquake', color: '#8b4513', description: 'Click to create shockwaves through ground.', density: 0 },
  { id: ElementType.TSUNAMI, name: 'Tsunami', color: '#0066cc', description: 'Click to summon a massive water wave.', density: 0 },
];

export const HIDDEN_ELEMENTS: Element[] = [
  { id: ElementType.FLOWER, name: 'Flower', color: '#fb7185', description: 'A decorative flower that grows from mature plants.', density: 0.5, thermalConductivity: 0.5, flammable: true },
  { id: ElementType.EMBER, name: 'Ember', color: '#f97316', description: 'Hot flying ember from explosions.', density: 0.15, thermalConductivity: 0.7, flammable: false },
];

export const FLOWER_ELEMENT = HIDDEN_ELEMENTS[0];

// Pre-built Scenes Interface
export interface PrebuiltScene {
  id: string;
  name: string;
  description: string;
  generate: (width: number, height: number) => Grid;
}

// Helper to create empty grid
const createEmptyGrid = (width: number, height: number): Grid => {
  return Array.from({ length: height }, () =>
    Array.from({ length: width }, () => ({ elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE }))
  );
};

// Pre-built Scenes
export const PREBUILT_SCENES: PrebuiltScene[] = [
  {
    id: 'volcano',
    name: 'Volcano',
    description: 'A towering mountain with a bubbling lava core. Watch as lava flows down the slopes!',
    generate: (width: number, height: number): Grid => {
      const grid = createEmptyGrid(width, height);
      const centerX = Math.floor(width / 2);
      const baseY = height - 1;
      
      // Create mountain shape
      for (let y = 0; y < height; y++) {
        const progress = y / height;
        const mountainWidth = Math.floor((width * 0.8) * (1 - progress * 0.7));
        const startX = centerX - Math.floor(mountainWidth / 2);
        const endX = centerX + Math.floor(mountainWidth / 2);
        
        for (let x = startX; x <= endX; x++) {
          if (x >= 0 && x < width && y < height - 5) {
            // Stone mountain body
            grid[y][x] = { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE };
          }
        }
      }
      
      // Create lava core at top
      const lavaCenterY = Math.floor(height * 0.25);
      const lavaRadius = 8;
      for (let dy = -lavaRadius; dy <= lavaRadius; dy++) {
        for (let dx = -lavaRadius; dx <= lavaRadius; dx++) {
          if (dx * dx + dy * dy <= lavaRadius * lavaRadius) {
            const x = centerX + dx;
            const y = lavaCenterY + dy;
            if (x >= 0 && x < width && y >= 0 && y < height) {
              grid[y][x] = { elementId: ElementType.LAVA, temperature: LAVA_TEMPERATURE };
            }
          }
        }
      }
      
      // Add lava channels flowing down
      const channels = [centerX - 5, centerX, centerX + 5];
      channels.forEach(channelX => {
        for (let y = lavaCenterY + lavaRadius; y < height - 5; y++) {
          const offset = Math.floor(Math.sin(y * 0.2) * 3);
          const x = channelX + offset;
          if (x >= 0 && x < width && y >= 0 && y < height) {
            if (grid[y][x].elementId === ElementType.STONE && Math.random() < 0.7) {
              grid[y][x] = { elementId: ElementType.LAVA, temperature: LAVA_TEMPERATURE };
            }
          }
        }
      });
      
      return grid;
    },
  },
  {
    id: 'waterfall',
    name: 'Waterfall',
    description: 'Cascading water flowing over rocky cliffs. A beautiful natural scene.',
    generate: (width: number, height: number): Grid => {
      const grid = createEmptyGrid(width, height);
      const cliffX = Math.floor(width * 0.6);
      
      // Create cliff structure
      for (let y = 0; y < height; y++) {
        const cliffWidth = 15 + Math.floor(Math.sin(y * 0.1) * 5);
        for (let x = cliffX; x < Math.min(cliffX + cliffWidth, width); x++) {
          grid[y][x] = { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE };
        }
      }
      
      // Create water source at top
      for (let x = 0; x < cliffX - 2; x++) {
        for (let y = 0; y < 8; y++) {
          grid[y][x] = { elementId: ElementType.WATER, temperature: AMBIENT_TEMPERATURE };
        }
      }
      
      // Create pool at bottom
      for (let x = 0; x < width; x++) {
        for (let y = height - 15; y < height; y++) {
          if (grid[y][x].elementId === ElementType.EMPTY) {
            grid[y][x] = { elementId: ElementType.WATER, temperature: AMBIENT_TEMPERATURE };
          }
        }
      }
      
      // Add some rocks in the pool
      for (let i = 0; i < 20; i++) {
        const rx = Math.floor(Math.random() * width);
        const ry = height - 5 - Math.floor(Math.random() * 8);
        if (rx >= 0 && rx < width && ry >= 0 && ry < height) {
          grid[ry][rx] = { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE };
        }
      }
      
      return grid;
    },
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'A living forest with trees, plants, and soil. Watch the ecosystem grow!',
    generate: (width: number, height: number): Grid => {
      const grid = createEmptyGrid(width, height);
      
      // Create ground
      const groundLevel = Math.floor(height * 0.75);
      for (let y = groundLevel; y < height; y++) {
        for (let x = 0; x < width; x++) {
          // Mix of dirt and mud
          grid[y][x] = { 
            elementId: Math.random() < 0.7 ? ElementType.DIRT : ElementType.MUD, 
            temperature: AMBIENT_TEMPERATURE 
          };
        }
      }
      
      // Create trees
      const numTrees = 8;
      for (let t = 0; t < numTrees; t++) {
        const treeX = Math.floor((width / (numTrees + 1)) * (t + 1));
        const treeHeight = 15 + Math.floor(Math.random() * 15);
        
        // Tree trunk
        for (let y = groundLevel - 1; y >= groundLevel - treeHeight && y >= 0; y--) {
          if (treeX >= 0 && treeX < width) {
            grid[y][treeX] = { elementId: ElementType.WOOD, temperature: AMBIENT_TEMPERATURE };
          }
        }
        
        // Tree canopy (leaves)
        const canopyY = groundLevel - treeHeight;
        const canopyRadius = 6 + Math.floor(Math.random() * 4);
        for (let dy = -canopyRadius; dy <= canopyRadius; dy++) {
          for (let dx = -canopyRadius; dx <= canopyRadius; dx++) {
            if (dx * dx + dy * dy <= canopyRadius * canopyRadius && Math.random() < 0.8) {
              const x = treeX + dx;
              const y = canopyY + dy;
              if (x >= 0 && x < width && y >= 0 && y < height) {
                if (grid[y][x].elementId === ElementType.EMPTY) {
                  grid[y][x] = { elementId: ElementType.PLANT, temperature: AMBIENT_TEMPERATURE };
                }
              }
            }
          }
        }
      }
      
      // Add water source
      const waterX = Math.floor(width * 0.15);
      for (let y = groundLevel - 1; y < height; y++) {
        for (let x = waterX; x < waterX + 8; x++) {
          if (x >= 0 && x < width && y >= 0 && y < height) {
            grid[y][x] = { elementId: ElementType.WATER, temperature: AMBIENT_TEMPERATURE };
          }
        }
      }
      
      // Add some plants on the ground
      for (let x = 0; x < width; x += 3) {
        if (Math.random() < 0.4 && x >= 0 && x < width) {
          const plantY = groundLevel - 1;
          if (plantY >= 0 && plantY < height && grid[plantY][x].elementId === ElementType.EMPTY) {
            grid[plantY][x] = { elementId: ElementType.PLANT, temperature: AMBIENT_TEMPERATURE };
          }
        }
      }
      
      return grid;
    },
  },
  {
    id: 'castle',
    name: 'Castle',
    description: 'A stone fortress waiting to be destroyed. Perfect for testing spells and explosions!',
    generate: (width: number, height: number): Grid => {
      const grid = createEmptyGrid(width, height);
      const centerX = Math.floor(width / 2);
      const groundY = height - 10;
      
      // Castle base
      const baseWidth = 60;
      const baseHeight = 40;
      const baseStartX = centerX - Math.floor(baseWidth / 2);
      
      for (let y = groundY - baseHeight; y < groundY; y++) {
        for (let x = baseStartX; x < baseStartX + baseWidth; x++) {
          if (x >= 0 && x < width && y >= 0 && y < height) {
            // Walls are stone, interior is empty
            const isWall = x < baseStartX + 4 || x >= baseStartX + baseWidth - 4 || 
                          y < groundY - baseHeight + 4 || y >= groundY - 4;
            if (isWall) {
              grid[y][x] = { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE };
            }
          }
        }
      }
      
      // Towers at corners
      const towerSize = 12;
      const towers = [
        { x: baseStartX, y: groundY - baseHeight },
        { x: baseStartX + baseWidth - towerSize, y: groundY - baseHeight },
      ];
      
      towers.forEach(tower => {
        for (let dy = 0; dy < baseHeight + 15; dy++) {
          for (let dx = 0; dx < towerSize; dx++) {
            const x = tower.x + dx;
            const y = tower.y - dy;
            if (x >= 0 && x < width && y >= 0 && y < height) {
              grid[y][x] = { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE };
            }
          }
        }
      });
      
      // Ground
      for (let y = groundY; y < height; y++) {
        for (let x = 0; x < width; x++) {
          grid[y][x] = { elementId: ElementType.DIRT, temperature: AMBIENT_TEMPERATURE };
        }
      }
      
      // Add some wood structures inside
      for (let x = centerX - 10; x < centerX + 10; x++) {
        for (let y = groundY - 15; y < groundY - 5; y++) {
          if (x >= 0 && x < width && y >= 0 && y < height) {
            if (grid[y][x].elementId === ElementType.EMPTY && Math.random() < 0.3) {
              grid[y][x] = { elementId: ElementType.WOOD, temperature: AMBIENT_TEMPERATURE };
            }
          }
        }
      }
      
      // Add gunpowder barrels (hidden)
      for (let i = 0; i < 5; i++) {
        const bx = centerX + Math.floor((Math.random() - 0.5) * 30);
        const by = groundY - 20 + Math.floor(Math.random() * 10);
        if (bx >= 0 && bx < width && by >= 0 && by < height) {
          if (grid[by][bx].elementId === ElementType.EMPTY) {
            grid[by][bx] = { elementId: ElementType.GUNPOWDER, temperature: AMBIENT_TEMPERATURE };
          }
        }
      }
      
      return grid;
    },
  },
  {
    id: 'blank',
    name: 'Blank Canvas',
    description: 'Start with a completely empty grid. Your creativity is the limit!',
    generate: (width: number, height: number): Grid => {
      return createEmptyGrid(width, height);
    },
  },
];
